/**
 * Strom kalendáře: transformace `GetPlanningTreeResponse` (generovaný read model)
 * na `TreeData` pro checkable TreeView a na zploštělé možnosti sérií pro zakládací popover.
 * ID uzlů jsou surová UUID entit — termíny tak jdou párovat s `CalendarTermResponse.termId`.
 */
import { createElement } from 'react';
import { CalendarDays, FolderKanban, Layers3, Package2 } from 'lucide-react';
import type { GetPlanningTreeResponse } from '@/api/generated/schemas';
import type { TreeData, TreeNodeType } from '@/shared/ui';
import { cn } from '@/shared/lib/utils';
import type { CalendarSerieOption } from './components/CreateTermsPopover';

/**
 * TreeView velikost typových ikon nevynucuje (ikona si ji vlastní) —
 * Lucide glyfy proto obalíme na 12 px odpovídajících hustotě stromu.
 */
function sizedTreeIcon(icon: React.FC<{ className?: string }>): React.FC<{ className?: string }> {
  return function SizedTreeIcon({ className }: { className?: string }) {
    return createElement(icon, { className: cn('h-3 w-3', className) });
  };
}

const ProjectIcon = sizedTreeIcon(FolderKanban);
const ProductIcon = sizedTreeIcon(Package2);
const SerieIcon = sizedTreeIcon(Layers3);
const TermIcon = sizedTreeIcon(CalendarDays);

export interface CalendarTreeLabels {
  project: string;
  product: string;
  serie: string;
  term: string;
}

export function buildCalendarTreeData(
  response: GetPlanningTreeResponse,
  labels: CalendarTreeLabels
): TreeData {
  const nodesById: TreeData['nodesById'] = {};
  const rootIds: string[] = [];

  for (const project of response.projects) {
    rootIds.push(project.projectId);
    nodesById[project.projectId] = {
      id: project.projectId,
      label: project.name,
      type: 'project',
      parentId: null,
      childIds: project.products.map((product) => product.productId),
    };

    for (const product of project.products) {
      nodesById[product.productId] = {
        id: product.productId,
        label: product.name,
        type: 'product',
        parentId: project.projectId,
        childIds: product.series.map((serie) => serie.productSerieId),
      };

      for (const serie of product.series) {
        nodesById[serie.productSerieId] = {
          id: serie.productSerieId,
          label: serie.name,
          type: 'serie',
          parentId: product.productId,
          childIds: serie.terms.map((term) => term.termId),
        };

        for (const term of serie.terms) {
          nodesById[term.termId] = {
            id: term.termId,
            label: term.name,
            type: 'term',
            parentId: serie.productSerieId,
            childIds: [],
          };
        }
      }
    }
  }

  const typeDefinitions: TreeNodeType[] = [
    { id: 'project', label: labels.project, icon: ProjectIcon },
    { id: 'product', label: labels.product, icon: ProductIcon },
    { id: 'serie', label: labels.serie, icon: SerieIcon },
    { id: 'term', label: labels.term, icon: TermIcon },
  ];

  return { nodesById, rootIds, typeDefinitions };
}

/** Všechna term id ve stromu (výchozí „vše zaškrtnuto“ + dohledání nově vzniklých termínů) */
export function collectTreeTermIds(response: GetPlanningTreeResponse): string[] {
  return response.projects.flatMap((project) =>
    project.products.flatMap((product) =>
      product.series.flatMap((serie) => serie.terms.map((term) => term.termId))
    )
  );
}

/** Zploštělé série pro select v zakládacím popoveru („Produkt — Série“) */
export function buildCalendarSerieOptions(
  response: GetPlanningTreeResponse
): CalendarSerieOption[] {
  return response.projects.flatMap((project) =>
    project.products.flatMap((product) =>
      product.series
        .filter((serie) => !serie.isArchived)
        .map((serie) => ({
          productId: product.productId,
          serieId: serie.productSerieId,
          label: `${product.name} — ${serie.name}`,
          nights: Math.max(0, serie.mainProgramLengthDays - 1),
        }))
    )
  );
}
